import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  Divider,
  Button,
  Badge,
  Tooltip,
  useMediaQuery,
  alpha,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import PersonIcon from '@mui/icons-material/Person';
import LogoutIcon from '@mui/icons-material/Logout';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import NotificationsIcon from '@mui/icons-material/Notifications';
import PaletteIcon from '@mui/icons-material/Palette';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import HubIcon from '@mui/icons-material/Hub';

import { logout, setSelectedClinic } from 'src/modules/auth/store/authActions';
import { getClinics } from 'src/modules/auth/api/authApi';
import { fetchNotifications } from 'src/modules/app/api/appApi';
import UpdateProfile from 'src/modules/app/container/ProfileContainer';
import FormDialog from 'src/modules/app/components/forms/FormDialog';
import AppearanceModal from 'src/components/App/AppearanceModal';
import ConnectModal from 'src/components/App/ConnectModal';
import { useAppearance } from 'src/context/AppearanceContext';
import { playNotificationSound } from 'src/utils/notifySound';

const Header = ({ onDrawerToggle }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { isDark, toggleDarkMode } = useAppearance();

  const [anchorElUser, setAnchorElUser] = useState(null);
  const [anchorElClinic, setAnchorElClinic] = useState(null);
  const [openProfile, setOpenProfile] = useState(false);
  const [openAppearance, setOpenAppearance] = useState(false);
  const [openConnect, setOpenConnect] = useState(false);
  const [clinicsList, setClinicsList] = useState([]);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const prevUnreadRef = useRef(null);

  const reduxClinic = useSelector((state) => state.auth?.selectedClinic);
  const reduxClinicsList = useSelector((state) => state.auth?.getClinic);

  const staffName = localStorage.getItem('staffName') || localStorage.getItem('STAFFNAME') || 'Staff Member';

  // Read stored clinic
  const getStoredClinic = () => {
    const selectedClinicStr = localStorage.getItem('selectedClinic');
    try {
      return selectedClinicStr ? JSON.parse(selectedClinicStr) : null;
    } catch {
      return null;
    }
  };

  const [activeClinic, setActiveClinic] = useState(getStoredClinic());

  // Load clinics list on mount
  useEffect(() => {
    const loadClinics = async () => {
      try {
        const res = await getClinics();
        const list = res?.data || (Array.isArray(res) ? res : []);
        if (Array.isArray(list) && list.length > 0) {
          setClinicsList(list);
          // If no active clinic selected yet, select the first one
          if (!activeClinic) {
            const firstClinic = list[0];
            setActiveClinic(firstClinic);
            localStorage.setItem('selectedClinic', JSON.stringify(firstClinic));
            localStorage.setItem('selectedClinicId', firstClinic.id);
            dispatch(setSelectedClinic(firstClinic));
          }
        }
      } catch (err) {
        console.error('Error fetching clinics in Header:', err);
      }
    };

    loadClinics();
  }, []);

  // Fetch real-time notification count
  useEffect(() => {
    const loadNotifCount = async () => {
      try {
        const res = await fetchNotifications(1, 10);
        let count = 0;
        if (res?.meta?.unreadCount !== undefined) {
          count = res.meta.unreadCount;
        } else if (res?.data) {
          count = res.data.filter((n) => n.notificationStatus === 'uncheck').length;
        }

        if (prevUnreadRef.current !== null && count > prevUnreadRef.current) {
          try {
            playNotificationSound();
          } catch (_) {}
        }
        prevUnreadRef.current = count;
        setUnreadNotifications(count);
      } catch (e) {
        // quiet fallback
      }
    };

    loadNotifCount();
    window.addEventListener('notificationsUpdated', loadNotifCount);
    window.addEventListener('appointmentCreated', loadNotifCount);
    window.addEventListener('taskCreated', loadNotifCount);
    window.addEventListener('queueUpdated', loadNotifCount);

    const interval = setInterval(loadNotifCount, 4000);
    return () => {
      window.removeEventListener('notificationsUpdated', loadNotifCount);
      window.removeEventListener('appointmentCreated', loadNotifCount);
      window.removeEventListener('taskCreated', loadNotifCount);
      window.removeEventListener('queueUpdated', loadNotifCount);
      clearInterval(interval);
    };
  }, [location.pathname]);

  // Sync Redux clinic changes to local state
  useEffect(() => {
    if (reduxClinic && reduxClinic.id && reduxClinic.id !== activeClinic?.id) {
      setActiveClinic(reduxClinic);
    }
  }, [reduxClinic]);

  // Sync Redux clinic list
  useEffect(() => {
    if (Array.isArray(reduxClinicsList) && reduxClinicsList.length > 0) {
      setClinicsList(reduxClinicsList);
    }
  }, [reduxClinicsList]);

  const handleOpenClinicMenu = (event) => {
    setAnchorElClinic(event.currentTarget);
  };

  const handleCloseClinicMenu = () => {
    setAnchorElClinic(null);
  };

  const handleSelectClinic = (clinic) => {
    setActiveClinic(clinic);
    localStorage.setItem('selectedClinic', JSON.stringify(clinic));
    localStorage.setItem('selectedClinicId', clinic.id);
    dispatch(setSelectedClinic(clinic));
    handleCloseClinicMenu();

    window.location.reload();
  };

  const handleOpenUserMenu = (event) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleLogout = () => {
    dispatch(logout());
  };

  return (
    <Box
      component="header"
      sx={{
        width: '100%',
        height: 70,
        bgcolor: theme.palette.background.paper,
        borderBottom: `1px solid ${theme.palette.divider}`,
        px: { xs: 1.5, sm: 3 },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        position: 'sticky',
        top: 0,
        zIndex: 1000,
        backdropFilter: 'blur(8px)',
        transition: 'background-color 0.2s ease, border-color 0.2s ease',
      }}
    >
      {/* Left Section: Mobile Menu Toggle & Interactive Clinic Switcher */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        {isMobile && (
          <IconButton
            onClick={onDrawerToggle}
            sx={{
              color: theme.palette.text.primary,
              bgcolor: theme.palette.background.subtle,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: '10px',
              p: 1,
            }}
          >
            <MenuIcon fontSize="small" />
          </IconButton>
        )}

        {/* Interactive Clinic Selector Dropdown */}
        <Button
          onClick={handleOpenClinicMenu}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            bgcolor: theme.palette.background.subtle,
            border: `1.5px solid ${theme.palette.divider}`,
            borderRadius: '12px',
            px: { xs: 1.5, sm: 2 },
            py: 0.8,
            textTransform: 'none',
            color: theme.palette.text.primary,
            transition: 'all 0.2s ease',
            '&:hover': {
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              borderColor: theme.palette.primary.main,
            },
          }}
        >
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: '#22C55E',
              boxShadow: '0 0 0 3px rgba(34, 197, 94, 0.2)',
              flexShrink: 0,
            }}
          />
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 700,
              color: theme.palette.text.primary,
              maxWidth: { xs: 130, sm: 220 },
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontSize: { xs: '13px', sm: '14px' },
            }}
          >
            {activeClinic ? activeClinic.clinicName : 'Select Clinic...'}
          </Typography>
          <KeyboardArrowDownIcon sx={{ color: theme.palette.text.secondary, fontSize: 18 }} />
        </Button>

        {/* Clinic Selection Menu */}
        <Menu
          anchorEl={anchorElClinic}
          open={Boolean(anchorElClinic)}
          onClose={handleCloseClinicMenu}
          PaperProps={{
            sx: {
              width: { xs: 260, sm: 300 },
              maxHeight: 360,
              borderRadius: '14px',
              boxShadow: isDark ? '0 12px 30px rgba(0,0,0,0.6)' : '0 12px 30px rgba(0,0,0,0.12)',
              border: `1px solid ${theme.palette.divider}`,
              bgcolor: theme.palette.background.paper,
              mt: 1,
              p: 0.5,
            },
          }}
        >
          <Box sx={{ px: 2, py: 1 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: theme.palette.text.secondary, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Switch Assigned Clinic
            </Typography>
          </Box>
          <Divider sx={{ my: 0.5, borderColor: theme.palette.divider }} />

          {clinicsList.length === 0 ? (
            <Box sx={{ px: 2, py: 2, textAlign: 'center' }}>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                {activeClinic ? activeClinic.clinicName : 'No other clinics available'}
              </Typography>
            </Box>
          ) : (
            clinicsList.map((clinic) => {
              const isSelected = activeClinic && activeClinic.id === clinic.id;
              return (
                <MenuItem
                  key={clinic.id}
                  onClick={() => handleSelectClinic(clinic)}
                  sx={{
                    borderRadius: '10px',
                    my: 0.5,
                    py: 1.2,
                    px: 1.5,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    bgcolor: isSelected ? alpha(theme.palette.primary.main, isDark ? 0.2 : 0.1) : 'transparent',
                    '&:hover': {
                      bgcolor: isSelected
                        ? alpha(theme.palette.primary.main, isDark ? 0.3 : 0.15)
                        : alpha(theme.palette.text.primary, 0.04),
                    },
                  }}
                >
                  <Box sx={{ pr: 1 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: isSelected ? 800 : 600,
                        color: isSelected ? theme.palette.primary.main : theme.palette.text.primary,
                        fontSize: '13.5px',
                      }}
                    >
                      {clinic.clinicName}
                    </Typography>
                    {clinic.address && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: theme.palette.text.secondary,
                          display: '-webkit-box',
                          WebkitLineClamp: 1,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          fontSize: '11px',
                        }}
                      >
                        {clinic.address}
                      </Typography>
                    )}
                  </Box>
                  {isSelected && (
                    <CheckCircleIcon sx={{ color: theme.palette.primary.main, fontSize: 18 }} />
                  )}
                </MenuItem>
              );
            })
          )}
        </Menu>
      </Box>

      {/* Right Section: Appearance Switcher, Notifications & Staff Profile */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5 } }}>
        {/* Quick Appearance & Theme Modal Toggle */}
        <Tooltip title="Appearance & Themes">
          <IconButton
            onClick={() => setOpenAppearance(true)}
            sx={{
              bgcolor: theme.palette.background.subtle,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: '12px',
              p: 1,
              transition: 'all 0.2s',
              color: theme.palette.text.secondary,
              '&:hover': {
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.main,
                borderColor: theme.palette.primary.main,
              },
            }}
          >
            <PaletteIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>

        {/* Quick One-Click Dark/Light Mode Switch */}
        <Tooltip title={isDark ? 'Switch to Light Mode' : 'Switch to Dark (Night Desk) Mode'}>
          <IconButton
            onClick={toggleDarkMode}
            sx={{
              bgcolor: theme.palette.background.subtle,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: '12px',
              p: 1,
              transition: 'all 0.2s',
              color: isDark ? '#F59E0B' : '#6366F1',
              '&:hover': {
                bgcolor: alpha(isDark ? '#F59E0B' : '#6366F1', 0.12),
              },
            }}
          >
            {isDark ? <LightModeIcon sx={{ fontSize: 20 }} /> : <DarkModeIcon sx={{ fontSize: 20 }} />}
          </IconButton>
        </Tooltip>

        {/* Notifications Icon Button */}
        <Tooltip title="Notifications">
          <IconButton
            onClick={() => navigate('/notifications')}
            sx={{
              bgcolor: theme.palette.background.subtle,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: '12px',
              p: 1,
              transition: 'all 0.2s',
              color: theme.palette.text.secondary,
              '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main },
            }}
          >
            <Badge badgeContent={unreadNotifications > 0 ? unreadNotifications : null} color="error" max={99}>
              <NotificationsIcon sx={{ fontSize: 20 }} />
            </Badge>
          </IconButton>
        </Tooltip>

        {/* Staff Profile Popover Toggle */}
        <Box
          onClick={handleOpenUserMenu}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.2,
            cursor: 'pointer',
            p: 0.6,
            pr: { xs: 0.6, sm: 1.5 },
            borderRadius: '12px',
            bgcolor: theme.palette.background.subtle,
            border: `1px solid ${theme.palette.divider}`,
            transition: 'all 0.2s',
            '&:hover': {
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              borderColor: theme.palette.primary.main,
            },
          }}
        >
          <Avatar
            sx={{
              width: 34,
              height: 34,
              bgcolor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText || '#FFFFFF',
              fontWeight: 700,
              fontSize: '13px',
            }}
          >
            {staffName.charAt(0).toUpperCase()}
          </Avatar>
          {!isMobile && (
            <Box sx={{ textAlign: 'left' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: theme.palette.text.primary, lineHeight: 1.2, fontSize: '13px' }}>
                {staffName}
              </Typography>
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: '11px' }}>
                Staff Desk
              </Typography>
            </Box>
          )}
          <KeyboardArrowDownIcon sx={{ color: theme.palette.text.secondary, fontSize: 16 }} />
        </Box>
      </Box>

      {/* User Menu Popover */}
      <Menu
        anchorEl={anchorElUser}
        open={Boolean(anchorElUser)}
        onClose={handleCloseUserMenu}
        PaperProps={{
          sx: {
            width: 230,
            borderRadius: '14px',
            boxShadow: isDark ? '0 10px 25px rgba(0,0,0,0.6)' : '0 10px 25px rgba(0,0,0,0.1)',
            border: `1px solid ${theme.palette.divider}`,
            bgcolor: theme.palette.background.paper,
            mt: 1.5,
          },
        }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
            {staffName}
          </Typography>
          <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
            {activeClinic?.clinicName || 'Clinic Staff'}
          </Typography>
        </Box>
        <Divider sx={{ borderColor: theme.palette.divider }} />
        
        <MenuItem
          onClick={() => {
            handleCloseUserMenu();
            setOpenAppearance(true);
          }}
          sx={{ py: 1.2 }}
        >
          <ListItemIcon>
            <PaletteIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
          </ListItemIcon>
          Appearance & Theme
        </MenuItem>

        <MenuItem
          onClick={() => {
            handleCloseUserMenu();
            setOpenProfile(true);
          }}
          sx={{ py: 1.2 }}
        >
          <ListItemIcon>
            <PersonIcon fontSize="small" />
          </ListItemIcon>
          Profile Settings
        </MenuItem>

        <MenuItem
          onClick={() => {
            handleCloseUserMenu();
            setOpenConnect(true);
          }}
          sx={{ py: 1.2 }}
        >
          <ListItemIcon>
            <HubIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
          </ListItemIcon>
          Connect with Us!
        </MenuItem>

        <MenuItem
          onClick={() => {
            handleCloseUserMenu();
            navigate('/notifications');
          }}
          sx={{ py: 1.2 }}
        >
          <ListItemIcon>
            <NotificationsIcon fontSize="small" />
          </ListItemIcon>
          Notifications
        </MenuItem>

        <Divider sx={{ borderColor: theme.palette.divider }} />
        <MenuItem onClick={handleLogout} sx={{ color: '#DC2626', py: 1.2 }}>
          <ListItemIcon sx={{ color: '#DC2626' }}>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          Logout
        </MenuItem>
      </Menu>

      {/* Appearance Settings Modal */}
      <AppearanceModal
        open={openAppearance}
        onClose={() => setOpenAppearance(false)}
      />

      {/* Connect With Us Modal */}
      <ConnectModal
        open={openConnect}
        onClose={() => setOpenConnect(false)}
      />

      {/* Profile Modal */}
      {openProfile && (
        <FormDialog open={openProfile} onClose={() => setOpenProfile(false)}>
          <UpdateProfile onClose={() => setOpenProfile(false)} />
        </FormDialog>
      )}
    </Box>
  );
};

export default Header;
